(() => {
  const games = [
    ['pokemon', 'Pokémon'], ['one-piece', 'One Piece'], ['dragon-ball', 'Dragon Ball'],
    ['magic', 'Magic'], ['union-arena', 'Union Arena'], ['lorcana', 'Lorcana'], ['gundam', 'Gundam'],
    ['comics', 'Comics'], ['sports', 'Sports'], ['miscellaneous', 'Miscellaneous']
  ];

  function normalize(value) {
    return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase().replace(/['’]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function gameFor(product) {
    const type = normalize(product.product_type);
    const source = (product._sourceCollections || []).map(normalize);
    const match = games.find(([key]) => type.includes(key) || source.some(handle => handle === key || handle.startsWith(`${key}-`)));
    return match?.[0] || 'miscellaneous';
  }

  function expansionTags(product) {
    const ignored = new Set(['promo', 'promos', 'stamp', 'stamped', 'sealed-promo', 'sealed-promos', 'single', 'singles', 'graded', 'sealed', 'xxl', '3xl', '4xl', '5xl', 'xl', 'l', 'm', 's']);
    return (product.tags || []).map(tag => String(tag).trim()).filter(tag => {
      if (!tag || ignored.has(tag.toLowerCase())) return false;
      return !tag.includes(':') || /^set:\s*\S/i.test(tag);
    });
  }

  function seriesDefinition(collection, game) {
    const prefix = new RegExp(`^${game}-(?:singles|graded|sealed)-`);
    if (!prefix.test(collection.handle)) return null;
    const label = collection.title.replace(/^.*?(?:Singles|Graded|Sealed)\s*-\s*/i, '');
    return { key: `${game}:${normalize(label)}`, label, tags: collection.tags || [] };
  }

  function orderKey(value) {
    return normalize(value).replace(/-and-/g, '-');
  }

  function orderIndex(values) {
    return new Map((values || []).map((value, index) => [orderKey(value), index]));
  }

  function compareByOrder(first, second, order) {
    const firstRank = order.get(orderKey(first)) ?? Number.MAX_SAFE_INTEGER;
    const secondRank = order.get(orderKey(second)) ?? Number.MAX_SAFE_INTEGER;
    return firstRank - secondRank || first.localeCompare(second);
  }

  function sortGroups(groups, game, releaseOrder) {
    const seriesOrder = orderIndex(releaseOrder.series?.[game]);
    const expansionOrder = orderIndex(releaseOrder.expansions);
    return groups.map(group => ({
      key: group.key, label: group.label,
      expansions: Array.from(group.expansions).sort((a, b) =>
        Number(/base/i.test(a)) - Number(/base/i.test(b)) || compareByOrder(a, b, expansionOrder))
    })).sort((a, b) => Number(a.key.endsWith(':other')) - Number(b.key.endsWith(':other')) ||
      compareByOrder(a.label, b.label, seriesOrder));
  }

  function groupsFor(game, products, definitions, releaseOrder = {}) {
    const available = new Map();
    products.filter(product => gameFor(product) === game).forEach(product => {
      expansionTags(product).forEach(tag => available.set(tag.toLowerCase(), tag));
    });
    const groups = new Map();
    const assigned = new Set();
    definitions.forEach(collection => {
      const definition = seriesDefinition(collection, game);
      if (!definition) return;
      const group = groups.get(definition.key) || { ...definition, expansions: new Set() };
      definition.tags.forEach(tag => {
        const actual = available.get(String(tag).toLowerCase());
        if (!actual) return;
        group.expansions.add(actual);
        assigned.add(actual.toLowerCase());
      });
      if (group.expansions.size) groups.set(group.key, group);
    });
    const remaining = Array.from(available.values()).filter(tag => !assigned.has(tag.toLowerCase()));
    if (remaining.length) groups.set(`${game}:other`, {
      key: `${game}:other`, label: groups.size ? 'Other expansions' : 'Sets & expansions', expansions: new Set(remaining)
    });
    return sortGroups(Array.from(groups.values()), game, releaseOrder);
  }

  function matches(product, selectedGames, seriesFilters) {
    const game = gameFor(product);
    if (selectedGames.length && !selectedGames.includes(game)) return false;
    const selectedSeries = seriesFilters.filter(series => series.game === game);
    if (!selectedSeries.length) return true;
    const tags = new Set((product.tags || []).map(tag => String(tag).toLowerCase()));
    return selectedSeries.some(series => {
      const wanted = series.checkedTags.length ? series.checkedTags : series.allTags;
      return wanted.some(tag => tags.has(tag));
    });
  }

  window.TMMCollectionGames = { games, gameFor, groupsFor, matches };
})();
